import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import formatLocalDate from 'helper/formatLocalDate';
import {
  Modelo, OpcaoRmv, PainelOptions, painelExibeTipoRodada,
} from 'helper/windx';
import {
  useGetPainelOptionsQuery,
  useGetModelosDisponiveisQuery,
} from 'services/api/windx';
import {
  selectWindxIdsModelos,
  selectWindxOpcoesRmv,
  selectWindxTipoRodada,
  selectWindxDataSimulacao,
  selectWindxTelaAtiva,
  setIdsModelos,
  setOpcoesRmv,
  setTipoRodada,
} from 'redux/reducers/windxControlPanelReducer';

const DIAS_D14 = 14;
const DIAS_D1 = 1;

function addDias(base: Date, dias: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + dias);
  return formatLocalDate(d);
}

export interface WindxControlPanelRules {
  opts: PainelOptions | undefined;
  isLoading: boolean;
  error: unknown;
  tela: 'sh' | 'sm' | 'comparativo';
  telaApi: 'sh' | 'sm';
  exibeRunType: boolean;
  exibeModelosRmv: boolean;
  exibePatamar: boolean;
  exibeFatorRen: boolean;
  exibeSimulacoesAntigas: boolean;
  /** "Estudo Automático" abre aberto: Semanal/Comparativo sem a etapa Tipo de Rodada. */
  abrirEstudoAutomatico: boolean;
  modoSelecao: 'radio' | 'checkbox';
  modelosVisiveis: Modelo[];
  idsSemDados: number[];
  datasRodadaPorId: Record<number, string>;
  opcoesRmvDisponiveis: OpcaoRmv[];
  dataMinima: string;
  dataMaxima: string;
}

/**
 * Regras de produto do Painel de Controle do Windx, compartilhadas por
 * desktop (`components/Windx/ControlPanel`) e mobile
 * (`components/Windx/Mobile/ControlPanelSheet`).
 *
 * Extraído do orquestrador desktop sem alteração de comportamento — inclusive
 * os efeitos que despacham (sincronização de RMV, auto-remoção de modelos
 * indisponíveis e, desde o WEOL-SM #99, o tipo 'final' assumido no SM sem
 * `tipos_rodada`). Eles PRECISAM viver aqui: se ficassem só no desktop, o
 * painel mobile perderia esses comportamentos e divergiria.
 */
export default function useControlPanelRules(): WindxControlPanelRules {
  const dispatch = useDispatch();

  const telaAtiva = useSelector(selectWindxTelaAtiva);
  // `tela` inclui 'comparativo' para que SavedPreferences separe
  // as preferencias de Semanal e Comparativo (item 2a, PO 2026-05-18).
  const tela = telaAtiva as 'sh' | 'sm' | 'comparativo';
  // Painel-options e modelos-disponiveis usam a API SM quando comparativo.
  const telaApi: 'sh' | 'sm' = tela === 'sh' ? 'sh' : 'sm';

  const { data: opts, isLoading, error } = useGetPainelOptionsQuery(telaApi);

  const idsModelos = useSelector(selectWindxIdsModelos);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);

  // WINDX-030: opcoesRmv eh array. Para modelos-disponiveis usamos boolean
  // escalar — quando ambos marcados, omitimos o filtro (BE retorna uniao).
  let comRmv: boolean | undefined;
  if (opcoesRmv.length === 1) {
    comRmv = opcoesRmv[0] === 'com_rmv';
  }

  // Contrato modelos-disponiveis v1.1 (2026-05-19): a rota aceita
  // `tipo_rodada` e `com_rmv` para alinhar disponibilidade com o filtro que
  // chart/data efetivamente usa. WEOL-SM (#99, contrato v1.5): o tipo vale
  // tambem no SM, onde a data de cada modelo passa a ser a da ultima rodada
  // DAQUELE tipo (omitido = 'final' no BE); por isso vai em qualquer aba
  // (antes so no SH).
  //
  // WINDX-034: dataSimulacao eh a unica fonte da verdade. Quando presente,
  // modo Simulacao Antiga (data_simulacao no payload); quando ausente, modo
  // Estudo Automatico (payload omite o campo, BE infere).
  const modelosDispParams: Parameters<typeof useGetModelosDisponiveisQuery>[0] = {
    tela: telaApi,
    ...(dataSimulacao ? { data_simulacao: dataSimulacao } : {}),
    ...(tipoRodada ? { tipo_rodada: tipoRodada } : {}),
    ...(comRmv !== undefined ? { com_rmv: comRmv } : {}),
  };

  const { data: modelosDisp } = useGetModelosDisponiveisQuery(modelosDispParams);

  const idsSemDados = useMemo<number[]>(() => {
    if (!modelosDisp) return [];
    return modelosDisp.modelos.filter((m) => !m.disponivel).map((m) => m.id);
  }, [modelosDisp]);

  const idsRetornadosBE = useMemo<Set<number> | null>(() => {
    if (!modelosDisp) return null;
    return new Set(modelosDisp.modelos.map((m) => m.id));
  }, [modelosDisp]);

  const datasRodadaPorId = useMemo<Record<number, string>>(() => {
    // ADR-018: `data_rodada` vem como string ISO 'YYYY-MM-DD' das rotas
    // /windx/* desde o MR BE !91.
    if (!modelosDisp) return {};
    return modelosDisp.modelos.reduce<Record<number, string>>((acc, m) => {
      if (m.data_rodada) acc[m.id] = m.data_rodada;
      return acc;
    }, {});
  }, [modelosDisp]);

  // WINDX-063 (ADR-042): o seletor lista os modelos de painel-options (ja
  // filtrados por `ativo` + tela no BE) que o modelos-disponiveis tambem
  // devolveu; enquanto ele carrega, o painel inteiro. WEOL-SM (#99): o SM e o
  // Comparativo cruzam como o SH, com o modelos-disponiveis DO TIPO escolhido —
  // daí vem os rotulos "(sem dados)" e "(apenas preliminar/final)" e o "Todos"
  // so com os elegiveis (`modeloElegivel`, helper/windx.ts). Acabou o fallback
  // aos 2 ONS fixos com painel-options vazio (um deles era o ONS Contingencia,
  // nunca populado): painel vazio, lista vazia, como no SH.
  const modelosVisiveis = useMemo<Modelo[]>(() => {
    if (!opts) return [];
    if (idsRetornadosBE === null) return opts.modelos;
    return opts.modelos.filter((m) => idsRetornadosBE.has(m.id));
  }, [opts, idsRetornadosBE]);

  // WINDX-040 (revisao 2026-05-25): UNIAO das opcoes de RMV entre modelos
  // selecionados (era intersecao em FE-LAYOUT-009/ADR-015). Operador pode
  // marcar 'sem_rmv' mesmo quando apenas alguns modelos suportam — BE
  // silenciosamente ignora modelos sem a variante e plota os outros.
  const opcoesRmvDisponiveis = useMemo<OpcaoRmv[]>(() => {
    if (!opts || idsModelos.length === 0) return [];
    const selecionados = opts.modelos.filter((m) => idsModelos.includes(m.id));
    if (selecionados.length === 0) return [];
    const uniao = new Set<OpcaoRmv>();
    selecionados.forEach((m) => m.opcoes_rmv.forEach((opt) => uniao.add(opt)));
    // Mantem ordem canonica ['com_rmv', 'sem_rmv'] para UX consistente.
    return (['com_rmv', 'sem_rmv'] as OpcaoRmv[]).filter((o) => uniao.has(o));
  }, [opts, idsModelos]);

  useEffect(() => {
    // WINDX-040: sincroniza opcoesRmv contra a UNIAO (nao mais intersecao).
    //
    // WINDX-056: quando NENHUM modelo esta selecionado (boot/limpar do
    // WEOL-SH), NAO forcamos ['com_rmv']. O painel abre sem default de RMV
    // para que a 1a chamada de modelos-disponiveis NAO filtre por com_rmv e a
    // ERA5_Climatologia (so preliminar+sem_rmv) apareca habilitada.
    if (opcoesRmvDisponiveis.length === 0) return;
    const validos = opcoesRmv.filter((o) => opcoesRmvDisponiveis.includes(o));
    if (validos.length === 0) {
      // Estado invalido apos troca de modelos: aplica default por capacidade.
      let proposto: OpcaoRmv[];
      if (opcoesRmvDisponiveis.length === 1) {
        proposto = [opcoesRmvDisponiveis[0]];
      } else if (opcoesRmvDisponiveis.includes('com_rmv')) {
        proposto = ['com_rmv'];
      } else {
        proposto = [opcoesRmvDisponiveis[0]];
      }
      dispatch(setOpcoesRmv(proposto));
    } else if (validos.length !== opcoesRmv.length) {
      dispatch(setOpcoesRmv(validos));
    }
  }, [opcoesRmv, opcoesRmvDisponiveis, dispatch]);

  // Auto-remove modelos que ficaram indisponiveis ou foram omitidos pelo BE.
  useEffect(() => {
    if (idsRetornadosBE === null && idsSemDados.length === 0) return;
    const selecaoValida = idsModelos.filter((id) => (
      !idsSemDados.includes(id)
      && (idsRetornadosBE === null || idsRetornadosBE.has(id))
    ));
    if (selecaoValida.length !== idsModelos.length) {
      dispatch(setIdsModelos(selecaoValida));
    }
  }, [idsSemDados, idsRetornadosBE, idsModelos, dispatch]);

  const hoje = new Date();
  const dataMinima = addDias(hoje, -DIAS_D14);
  const dataMaxima = addDias(hoje, -DIAS_D1);

  // WINDX-034: "Incluir Simulações Antigas" so em WEOL-SH. WEOL-SM (Semanal
  // e Comparativo) sempre usa MAX(data_rodada) por modelo (WINDX-028); no SM,
  // as rodadas passadas ficam no Comparativo (#99, decisao 5).
  const exibeSimulacoesAntigas = tela === 'sh';
  // Painel-options DESTA aba. Na troca de aba o RTK Query devolve em `data` o
  // painel da aba anterior ate o novo chegar (SH -> SM: o do SH, com
  // `tipos_rodada`). As regras do Tipo de Rodada usam so o painel da aba: senao
  // a etapa do SH apareceria no SM nessa janela, e um clique em "Preliminar"
  // ficaria preso quando o painel do SM chegasse sem a etapa (flag do BE
  // desligada) — sem o radio para trocar e sem o 'final' assumido abaixo. O BE
  // ecoa a `tela` pedida (Comparativo pede 'sm', `telaApi`).
  const painelDaTela = opts?.tela === telaApi ? opts : undefined;
  // A etapa Tipo de Rodada aparece quando o painel-options traz `tipos_rodada`:
  // sempre no SH; no SM/Comparativo so com a flag WINDX_SM_TIPOS_RODADA_ENABLED
  // ligada no BE (WEOL-SM #99). A regra (com a guarda do painel da aba) mora em
  // `painelExibeTipoRodada`, compartilhada com as Preferencias Salvas e o mobile.
  const exibeRunType = painelExibeTipoRodada(opts, telaApi);
  // Comparativo tambem exibe patamar (mesma granularidade de SM).
  const exibePatamar = tela === 'sm' || tela === 'comparativo';
  // Fator da REN (padronizacao 2026-05-28): acordeon opt-in em WEOL-SH e
  // WEOL-SM (Semanal). AUSENTE na sub-aba Comparativo (decisao de produto).
  const exibeFatorRen = tela === 'sh' || tela === 'sm';
  // WINDX-061: Modelos e RMV so aparecem DEPOIS que o usuario escolhe o Tipo
  // de Rodada, em qualquer aba com a etapa (exibeRunType). Sem `tipos_rodada`
  // no painel (SM/Comparativo com a flag do BE desligada), seguem sempre
  // visiveis. Em qualquer caso, so com o painel DESTA aba: na janela da troca
  // de aba o `exibeRunType` fica falso, e sem esta guarda a lista de modelos da
  // aba anterior apareceria por um instante (revisao da FE-3, #99).
  const exibeModelosRmv = !!painelDaTela && (!exibeRunType || !!tipoRodada);

  // WEOL-SM (#99) — compatibilidade com o BE de producao na transicao: com a
  // flag WINDX_SM_TIPOS_RODADA_ENABLED desligada (ou painel-options em cache
  // antigo), o SM/Comparativo vem SEM `tipos_rodada`. O painel segue como antes
  // (sem a etapa Tipo de Rodada), mas a consulta do SM exige um tipo
  // (selectWindxFiltrosCompletos) e os payloads (grafico, comparativo, download)
  // precisam de um tipo concreto: o hook assume 'final', o mesmo default do BE
  // para o campo omitido. So despacha com o tipo vazio (boot, troca SH <-> SM,
  // "Limpar filtros"; Semanal <-> Comparativo preserva o tipo), entao e
  // idempotente; e NAO dispara consulta — quem consulta continua sendo o botao
  // e os auto-triggers.
  const smSemEtapaTipoRodada = (tela === 'sm' || tela === 'comparativo')
    && !!painelDaTela && !exibeRunType;
  const assumeTipoFinal = smSemEtapaTipoRodada && !tipoRodada;
  useEffect(() => {
    if (assumeTipoFinal) dispatch(setTipoRodada('final'));
  }, [assumeTipoFinal, dispatch]);
  // Decisao do #99 (revisao da FE-2, 27/10): sem a etapa Tipo de Rodada, "Estudo
  // Automatico" abre ABERTO — o agrupamento nao pode custar um clique a mais
  // para chegar aos Modelos (no SM eles eram itens soltos do painel). Com a
  // etapa (SH sempre; SM com a flag ligada), abre fechado como antes.
  const abrirEstudoAutomatico = smSemEtapaTipoRodada;

  // WINDX-065: o modo deriva da SUB-ABA (painel compartilhado). Semanal (sm) e
  // SH = checkbox (multi-modelo); Comparativo = radio (single-model).
  const modoSelecao: 'radio' | 'checkbox' = telaAtiva === 'comparativo' ? 'radio' : 'checkbox';

  return {
    opts,
    isLoading,
    error,
    tela,
    telaApi,
    exibeRunType,
    exibeModelosRmv,
    exibePatamar,
    exibeFatorRen,
    exibeSimulacoesAntigas,
    abrirEstudoAutomatico,
    modoSelecao,
    modelosVisiveis,
    idsSemDados,
    datasRodadaPorId,
    opcoesRmvDisponiveis,
    dataMinima,
    dataMaxima,
  };
}
